import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { parseProducts, readDepreciatedProducts } from '../ps/format';
import psTheme, { backdrop } from '../ps/psTheme';
import '../ps/ps-tokens.css';
import '../ps/learn.css';
import loader from './loader.png';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './customVideoStyles.css';
import Hls from 'hls.js';
import { PlayerBar, TitleBlock } from './PlayerChrome';
import useSubtitles from './useSubtitles';
import TheCB from './TheCB';
import { hasVideoChat } from './videoChat';
import debounce from 'lodash.debounce';


const VPlayerOverlay = ({ identifier, onClose, progress = 0, isPromo }) => {
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null); // Add ref for HLS instance to prevent memory leak
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [missingProducts,setmissingProducts] = useState([]);
  const [productData, setProductData] = useState({});
  const [missingOpen, setMissingOpen] = useState(false);

  const lastProgressUpdate = useRef(0);

  // English by default, and whatever you pick from then on.
  const { selected: selectedSubtitle, select: handleSubtitleSelect } = useSubtitles({
    playerRef,
    subtitles: videoData?.subtitles,
    isPlayerReady,
  });


  const handleProgressUpdate = () => {
    let time = Math.round(currentTime);
    let duration = Math.round(videoDuration);

    if (playerRef.current) {
      time = Math.round(playerRef.current.currentTime());
      duration = Math.round(playerRef.current.duration());
    }

    if (duration > 0) {
      const progress = Math.floor((time / duration) * 100);
      if (progress !== lastProgressUpdate.current) {
        lastProgressUpdate.current = progress;
        const storedUserData = JSON.parse(localStorage.getItem('userData'));
        if (storedUserData && storedUserData.email) {
          axios.post('https://serve.philspeiser.com/api/setvideoprogress', {
            email: storedUserData.email,
            name: storedUserData.displayName,
            videoId: identifier,
            progress: progress,
          });
        }
      }
    }
  };


  const debouncedHandleProgressUpdate = debounce(handleProgressUpdate, 1000);

  useEffect(() => {
    if (isPlayerReady && playerRef.current && videoDuration > 0) {
      const seekTime = (progress / 100) * videoDuration;

      // Only seek if the seekTime is above 10 seconds and below 95% of the duration
      if (seekTime >= 10 && progress < 95) {
        playerRef.current.currentTime(seekTime);
      }
    }
  }, [isPlayerReady, videoDuration, progress]);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 900);

    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    if (playerRef.current && isPlayerReady) {
      const player = playerRef.current;

      const updateTime = () => {
        const newTime = Math.floor(player.currentTime());
        setCurrentTime(newTime);
      };

      player.on('timeupdate', updateTime);
      player.on('play', updateTime);

      const intervalId = setInterval(() => {
        if (player && !player.paused()) {
          debouncedHandleProgressUpdate();
        }
      }, 10000); // Update progress every 10 seconds

      return () => {
        player.off('timeupdate', updateTime);
        player.off('play', updateTime);
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef, isPlayerReady]);


  useEffect(() => {
    const handleBeforeUnload = () => {
      handleProgressUpdate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  // Escape closes the overlay, the way every other full-screen thing on the
  // web does. Clicking the corner was the only way out before.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        handleProgressUpdate();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, currentTime, videoDuration]);


  const orderSubtitles = (videoData) => {
    if (!videoData || !videoData.subtitles || !Array.isArray(videoData.subtitles)) {
      return [];
    }

    const languageOrder = ['en', 'de', 'fr', 'it', 'es'];

    const orderMap = Object.fromEntries(
      languageOrder.map((lang, index) => [lang, index])
    );

    const orderedSubtitles = [...videoData.subtitles].sort((a, b) => {
      const indexA = orderMap[a.srclang] ?? Number.MAX_SAFE_INTEGER;
      const indexB = orderMap[b.srclang] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });

    return orderedSubtitles;
  };

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const response = await axios.get(
          `https://learn.philspeiser.com/api/getvideo?identifier=${identifier}`
        );
        const videoInfo = response.data;

        if (videoInfo) {
          // 1. Replace the HLS and MP4 URLs (if present)
          if (videoInfo.assets) {
            // HLS
            if (
              videoInfo.assets.hls &&
              videoInfo.assets.hls.startsWith('https://vod.api.video/vod/')
            ) {
              videoInfo.assets.hls = videoInfo.assets.hls.replace(
                'https://vod.api.video/vod/',
                'https://psvids.b-cdn.net/'
              );
            }

            // MP4
            if (
              videoInfo.assets.mp4 &&
              videoInfo.assets.mp4.startsWith('https://vod.api.video/vod/')
            ) {
              videoInfo.assets.mp4 = videoInfo.assets.mp4.replace(
                'https://vod.api.video/vod/',
                'https://psvids.b-cdn.net/'
              );
            }
          }

          // 2. Replace the thumbnail URL if it points to vod.api.video
          if (
            videoInfo.thumbnailUrl &&
            videoInfo.thumbnailUrl.startsWith('https://vod.api.video/vod/')
          ) {
            videoInfo.thumbnailUrl = videoInfo.thumbnailUrl.replace(
              'https://vod.api.video/vod/',
              'https://psvids.b-cdn.net/'
            );
          }

          // Replace Vercel Blob Storage with BunnyCDN
          if (
            videoInfo.thumbnailUrl &&
            videoInfo.thumbnailUrl.includes('i6bcizwg1mpghn9b.public.blob.vercel-storage.com')
          ) {
            videoInfo.thumbnailUrl = videoInfo.thumbnailUrl.replace(
              'i6bcizwg1mpghn9b.public.blob.vercel-storage.com',
              'psblob.b-cdn.net'
            );
          }

          // 3. If you're also setting a fallback, do so afterward:
          videoInfo.thumbnail =
            videoInfo.thumbnailUrl ??
            'https://i6bcizwg1mpghn9b.public.blob.vercel-storage.com/videothumbnails/fallback-bDkvltu910WiV6ZVUREx2y3iICi8PQ.jpg';

          // 4. Order subtitles (existing logic)
          videoInfo.subtitles = orderSubtitles(videoInfo);

          setVideoData(videoInfo);
        }
      } catch (error) {
        // Error fetching video data
      }
    };

    fetchVideoData();
  }, [identifier]);


  useEffect(() => {
    return () => {
      if (playerRef.current) {
        handleProgressUpdate(); // Update progress on component unmount
      }
      // Clean up HLS instance to prevent memory leak
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // Clean up Video.js player
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



    useEffect(() => {
    if (!isPlayerReady) return; // Only run if the video is actually ready

    const owned = JSON.parse(localStorage.getItem('userOwnedProducts'));
    const prdcts = JSON.parse(localStorage.getItem('productInfo'));
    setProductData(prdcts);

    if (owned && videoData && videoData.products && owned.subscription !== 'active')  {
      // Retired plugins drop out of the upsell: this dialog links straight at
      // `productData[product].link`, and for a plugin that is no longer sold
      // that is a dead end. parseProducts rather than split(',') so a stored
      // "A, B" does not look for an entitlement to " B".
      const depreciated = readDepreciatedProducts(prdcts);
      const missing = parseProducts(videoData.products).filter(
        (product) => !owned.products.includes(product) && !depreciated.has(product)
      );
      setmissingProducts(missing);
    }
  }, [isPlayerReady, videoData]);




  const handleChapterClick = (time) => {
    if (playerRef.current) {
      const safeTime = time < videoDuration ? time : videoDuration - 1;

      playerRef.current.currentTime(Math.round(safeTime));
      playerRef.current.play();
    }
  };

  return (
    <ThemeProvider theme={psTheme}>
      <Box
        sx={{
          ...backdrop,
          position: 'fixed',
          inset: 0,
          // Below MUI's modal layer (1300) on purpose. At 3000 this sheet
          // covered every menu, dialog and tooltip opened from inside it --
          // they portal to the body, so they were rendering behind the very
          // overlay that owns them. See the ladder in learn.css.
          zIndex: 1250,
          overflowY: 'auto',
          fontFamily: 'var(--ps-font)',
        }}
      >
        {/* Quiet chip: no face until you go looking for it. */}
        <IconButton
          aria-label="Close"
          onClick={() => {
            handleProgressUpdate();
            onClose();
          }}
          sx={{
            position: 'fixed',
            top: { xs: 10, sm: 18 },
            right: { xs: 10, sm: 18 },
            zIndex: 20,
            color: 'var(--ps-text-2)',
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {loading && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'var(--ps-bg-deep)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <Box
              sx={{
                width: '45px',
                height: '45px',
                animation: 'loader-animation 0.8s steps(19) infinite',
                backgroundImage: `url(${loader})`,
                backgroundSize: '45px 885px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <style>
              {`
                @keyframes loader-animation {
                  0% { background-position: 0 0; }
                  100% { background-position: 0 -885px; }
                }
              `}
            </style>
          </Box>
        )}



        {videoData && (
          <Box
            sx={{
              width: { xs: '100%', sm: '72%' },
              margin: 'auto',
              px: { xs: '10px', sm: 0 },
              pt: { xs: '48px', sm: '56px' },
              pb: '96px',
              maxWidth: '1400px',
            }}
          >

            <div className="learn-stage">
              <div data-vjs-player>
                <video
                  ref={(el) => {
                    videoRef.current = el;

                    if (
                      el &&
                      videoData &&
                      videoData.assets &&
                      (videoData.assets.hls || videoData.assets.mp4) &&
                      !playerRef.current
                    ) {
                      const videoElement = el;

                      playerRef.current = videojs(videoElement, {
                        controls: true,
                        autoplay: true,
                        preload: 'auto',
                        poster: videoData.thumbnail,
                        responsive: true,
                        crossOrigin: 'anonymous',
                        fluid: true,
                        // Without this, iPhone Safari hijacks play into its
                        // native fullscreen player, where the custom skin and
                        // the overlay-rendered subtitles do not exist.
                        playsinline: true,
                        html5: {
                          nativeTextTracks: false,
                        },
                        controlBar: {
                          pictureInPictureToggle: false,
                          captionsButton: false,
                          subtitlesButton: false,
                          subsCapsButton: false,
                        },
                      });

                    // Add subtitles
                    if (videoData.subtitles && videoData.subtitles.length > 0) {
                      videoData.subtitles.forEach((subtitle) => {
                        playerRef.current.addRemoteTextTrack(
                          {
                            kind: 'subtitles',
                            label: subtitle.label,
                            srclang: subtitle.srclang,
                            src: subtitle.src,
                            default: subtitle.default,
                            crossOrigin: 'anonymous',
                          },
                          false
                        );
                      });
                    }

                    // Initialize HLS or set MP4 source
                    if (Hls.isSupported() && videoData.assets.hls) {
                      hlsRef.current = new Hls({
                        startLevel: 3,
                        capLevelToPlayerSize: false,
                        // The manifest carries the same captions the remote
                        // VTT tracks above already render (they exist for
                        // iPhone's native fullscreen player); parsing the
                        // manifest's copy too would show every cue twice.
                        enableWebVTT: false,
                        enableIMSC1: false,
                      });
                      hlsRef.current.loadSource(videoData.assets.hls);
                      hlsRef.current.attachMedia(videoElement);
                    } else if (
                      videoElement.canPlayType('application/vnd.apple.mpegurl') &&
                      videoData.assets.hls
                    ) {
                      videoElement.src = videoData.assets.hls;
                    } else if (videoData.assets.mp4) {
                      videoElement.src = videoData.assets.mp4;
                    } else {
                      // No compatible video format found
                    }

                    // Listen for metadata to get video duration
                    playerRef.current.on('loadedmetadata', () => {
                      const duration = playerRef.current.duration();
                      setVideoDuration(duration);

                      // Adjust chapters if necessary
                      if (videoData.chapters && videoData.chapters.length > 0) {
                        const adjustedChapters = videoData.chapters.map((chapter, index) => {
                          if (index === videoData.chapters.length - 1 && chapter.time >= duration) {
                            return { ...chapter, time: duration - 1 };
                          }
                          return chapter;
                        });
                        setVideoData((prevData) => ({
                          ...prevData,
                          chapters: adjustedChapters,
                        }));
                      }
                    });

                    // Error handling
                    playerRef.current.on('error', () => {
                      // Optionally, display a user-friendly message or attempt to recover
                    });

                      setIsPlayerReady(true);
                    }
                  }}
                  className="video-js vjs-big-play-centered custom-player"
                  crossOrigin="anonymous"
                  playsInline
                />
              </div>
            </div>

            <PlayerBar
              subtitles={videoData.subtitles}
              selectedSubtitle={selectedSubtitle}
              onSubtitleSelect={handleSubtitleSelect}
              chapters={videoData.chapters}
              currentTime={currentTime}
              duration={videoDuration}
              onChapterClick={handleChapterClick}
            />

            <TitleBlock video={videoData} />

            {/* Inline, where the products are listed -- it used to float over
                the bottom of the page, on top of the chat launcher. */}
            {missingProducts.length > 0 && (
              <Button onClick={() => setMissingOpen(true)} sx={{ mt: '22px' }}>
                Get the {missingProducts.length} plugin{missingProducts.length > 1 ? 's' : ''} you are missing
              </Button>
            )}

            {isPromo && videoData.link && videoData.linktext && (
              <Box sx={{ mt: '22px' }}>
                <Button
                  variant="contained"
                  component="a"
                  href={videoData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {videoData.linktext}
                </Button>
              </Box>
            )}

            {hasVideoChat(videoData) && (
              <TheCB identifier={identifier} timestamp={currentTime} />
            )}

            <Dialog open={missingOpen} onClose={() => setMissingOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Plugins you are missing</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {missingProducts.map((product) => (
                    <Button
                      key={product}
                      component="a"
                      href={productData?.[product]?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {product}
                    </Button>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMissingOpen(false)}>Close</Button>
                <Button
                  variant="contained"
                  component="a"
                  href="https://philspeiser.com/the_circle"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Unlock all products
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default VPlayerOverlay;
